import { urlFor } from '@/sanity/lib/image'

/**
 * Извлекает изображение из объекта mainImage, который может быть:
 * - обычным изображением Sanity (тип 'image')
 * - лицензированным изображением (тип 'licensedImage', где изображение в поле 'image')
 * - фото из библиотеки (тип 'photoAsset', где изображение в поле 'image')
 * - строкой (URL)
 * 
 * @param {Object|string} mainImage - Объект изображения из Sanity или строка URL
 * @returns {Object|null} Объект изображения для использования с urlFor, или null если не найдено
 */
export function getImageFromMainImage(mainImage) {
  if (!mainImage) {
    return null
  }

  // Если это строка (URL), возвращаем null (нужно обрабатывать отдельно)
  if (typeof mainImage === 'string') {
    return null
  }

  // Если это licensedImage, извлекаем изображение из поля image
  if (mainImage._type === 'licensedImage' && mainImage.image) {
    return mainImage.image
  }

  // Если это photoAsset (фото из библиотеки), извлекаем изображение из поля image
  if (mainImage._type === 'photoAsset' && mainImage.image) {
    return mainImage.image
  }

  // Если это обычное изображение Sanity
  if (mainImage._type === 'image') {
    return mainImage
  }

  // Если есть поле asset (прямая ссылка на asset)
  if (mainImage.asset) {
    return mainImage
  }

  return null
}

/**
 * Получает alt текст из объекта mainImage
 * 
 * @param {Object|string} mainImage - Объект изображения из Sanity или строка URL
 * @returns {string} Alt текст или пустая строка
 */
export function getImageAlt(mainImage) {
  if (!mainImage || typeof mainImage === 'string') {
    return ''
  }

  // Для photoAsset alt может быть в корневом поле alt
  if (mainImage._type === 'photoAsset' && mainImage.alt) {
    return mainImage.alt
  }

  // Для licensedImage alt может быть в image.alt
  if (mainImage._type === 'licensedImage' && mainImage.image?.alt) {
    return mainImage.image.alt
  }

  // Для обычного изображения
  if (mainImage.alt) {
    return mainImage.alt
  }

  return ''
}

/**
 * Генерирует URL изображения из mainImage с указанными размерами и параметрами качества
 * 
 * @param {Object|string} mainImage - Объект изображения из Sanity или строка URL
 * @param {Object} options - Опции для генерации URL
 * @param {number} options.width - Ширина изображения
 * @param {number} options.height - Высота изображения
 * @param {number} options.quality - Качество изображения (0-100, по умолчанию 90)
 * @param {string} options.format - Формат изображения ('webp', 'jpg', 'png', 'auto', по умолчанию 'auto')
 * @param {string} options.fit - Режим подгонки ('clip', 'crop', 'fill', 'fillmax', 'max', 'scale', 'min')
 * @returns {string|null} URL изображения или null если не удалось сгенерировать
 */
export function getImageUrl(mainImage, options = {}) {
  if (!mainImage) {
    return null
  }

  // Если это строка (URL), возвращаем как есть
  if (typeof mainImage === 'string') {
    return mainImage
  }

  // Извлекаем изображение
  const image = getImageFromMainImage(mainImage)
  if (!image) {
    return null
  }

  // Генерируем URL с помощью urlFor
  let builder = urlFor(image)

  if (options.width) {
    builder = builder.width(options.width)
  }

  if (options.height) {
    builder = builder.height(options.height)
  }

  // Устанавливаем качество (по умолчанию 90 для высокого качества)
  const quality = options.quality !== undefined ? options.quality : 90
  builder = builder.quality(quality)

  // Устанавливаем формат (по умолчанию 'auto' для автоматического выбора лучшего формата)
  const format = options.format || 'auto'
  if (format === 'auto') {
    builder = builder.auto('format')
  } else {
    builder = builder.format(format)
  }

  // Устанавливаем режим подгонки, если указан
  if (options.fit) {
    builder = builder.fit(options.fit)
  }

  try {
    return builder.url()
  } catch (error) {
    console.error('Ошибка при генерации URL изображения:', error)
    return null
  }
}

